import {
	type CartData,
	cartQueries,
	createShopifyRequestContext,
	createStorefrontClient,
	gql,
	type ProductPayload,
} from '@shopify/hydrogen';

export type ControlTrackingValues = { uniqueToken: string; visitToken: string; consent: string };
export type BrowserControlConfig = {
	storeDomain: string;
	storefrontId: string;
	publicToken: string;
};

/** Require the official same-origin consent response before the SDK getter can fall back. */
export function readBrowserControlTrackingValues(options: {
	privacy:
		| {
				consentStatus?: string;
				analyticsProcessingAllowed(): boolean;
				currentVisitorConsent(): Record<string, unknown>;
				shouldShowGDPRBanner(): boolean;
		  }
		| undefined;
	entries: PerformanceEntry[];
	origin: string;
	getTrackingValues(): ControlTrackingValues;
}): ControlTrackingValues | null {
	const { privacy } = options;
	if (
		privacy?.consentStatus !== 'loaded' ||
		!privacy.analyticsProcessingAllowed() ||
		privacy.currentVisitorConsent().analytics === 'no' ||
		privacy.shouldShowGDPRBanner()
	)
		return null;
	const observed = latestConsentResponse(options.entries, options.origin);
	if (!observed) return null;
	const official = options.getTrackingValues();
	return official.uniqueToken === observed.uniqueToken &&
		official.visitToken === observed.visitToken &&
		official.consent === observed.consent
		? official
		: null;
}

function latestConsentResponse(
	entries: PerformanceEntry[],
	origin: string,
): ControlTrackingValues | null {
	for (let index = entries.length - 1; index >= 0; index--) {
		const entry = entries[index] as PerformanceResourceTiming;
		if (entry.initiatorType !== 'fetch') continue;
		let url: URL;
		try {
			url = new URL(entry.name);
		} catch {
			continue;
		}
		if (url.origin !== origin || url.pathname !== '/api/unstable/graphql.json') continue;
		const timings = entry.serverTiming ?? [];
		const value = (name: string) =>
			[...timings].reverse().find((timing) => timing.name === name)?.description;
		const uniqueToken = value('_y');
		const visitToken = value('_s');
		const consent = value('_cmp');
		if (!uniqueToken || !visitToken || !consent) continue;
		return { uniqueToken, visitToken, consent };
	}
	return null;
}

const PRODUCT =
	gql(`query BrowserControlProduct($id: ID!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
  node(id: $id) { ... on ProductVariant {
    id title sku price { amount currencyCode }
    product { id title vendor productType }
  } }
}`);

/** One disposable cart, with no persisted cart ID, mutation retry, or server fallback. */
export function createBrowserCartControl(options: {
	config: BrowserControlConfig;
	trackingValues: ControlTrackingValues;
	pageUrl: string;
	signal: AbortSignal;
	consentStillValid: () => boolean;
	onProduct: (product: ProductPayload) => void;
	onCart: (cart: CartData) => void;
	fetch?: typeof fetch;
}) {
	const { config, trackingValues, signal } = options;
	if (
		!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(config.storeDomain) ||
		!/^\d+$/.test(config.storefrontId) ||
		!config.publicToken ||
		!trackingValues.uniqueToken ||
		!trackingValues.visitToken ||
		!trackingValues.consent
	) {
		throw new Error('Browser control configuration or consent identity is unavailable.');
	}
	const apiUrl = `https://${config.storeDomain}/api/2026-04/graphql.json`;
	const nativeFetch = options.fetch ?? globalThis.fetch;
	const context = createShopifyRequestContext({
		request: {
			url: options.pageUrl,
			signal,
			headers: new Headers({
				'X-Shopify-UniqueToken': trackingValues.uniqueToken,
				'X-Shopify-VisitToken': trackingValues.visitToken,
			}),
		},
		i18n: { country: 'US', language: 'EN' },
	});
	const client = createStorefrontClient({
		type: 'public',
		requestContext: context,
		config: {
			storeDomain: config.storeDomain,
			storefrontId: config.storefrontId,
			publicStorefrontToken: config.publicToken,
			defaultTimeoutInMs: 10_000,
			fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
				if (String(input) !== apiUrl) throw new Error('Unexpected browser control destination.');
				const headers = new Headers(init?.headers);
				// A browser supplies its real network identity and Origin/Referer itself.
				// First-party HttpOnly cookies cannot be forwarded cross-origin.
				for (const name of [...headers.keys()]) {
					if (
						name.startsWith('sec-') ||
						[
							'cookie',
							'authorization',
							'shopify-storefront-private-token',
							'shopify-storefront-buyer-ip',
							'origin',
							'referer',
							'user-agent',
							// This SDK header is absent from Shopify's browser CORS allowlist.
							'x-hydrogen-version',
						].includes(name)
					)
						headers.delete(name);
				}
				return nativeFetch(input, {
					...init,
					headers,
					cache: 'no-store',
					credentials: 'omit',
					mode: 'cors',
					redirect: 'error',
				});
			},
		},
	});
	let addAttempted = false;
	let removeAttempted = false;
	let running = false;
	let ownedCart: CartData | undefined;

	return {
		async add(variantId: string) {
			if (addAttempted || running || signal.aborted || !/^\d+$/.test(variantId)) return false;
			addAttempted = true;
			running = true;
			try {
				if (!options.consentStillValid())
					throw new Error('Consent identity changed. Open a fresh control.');
				const id = `gid://shopify/ProductVariant/${variantId}`;
				const productResult = await client.graphql(PRODUCT, { variables: { id } });
				const variant = productResult.data?.node;
				if (productResult.errors || !variant || !('product' in variant))
					throw new Error('The control product could not be loaded.');
				signal.throwIfAborted();
				if (!options.consentStillValid())
					throw new Error('Consent identity changed. No cart was created.');
				options.onProduct({
					id: variant.product.id,
					title: variant.product.title,
					vendor: variant.product.vendor,
					productType: variant.product.productType,
					price: variant.price.amount,
					variantId: variant.id,
					variantTitle: variant.title,
					sku: variant.sku,
					quantity: 1,
				});
				signal.throwIfAborted();
				const result = await client.graphql(cartQueries.cartCreate, {
					variables: { input: { lines: [{ merchandiseId: id, quantity: 1 }] } },
				});
				const payload = result.data?.cartCreate;
				// Retain an identifiable returned cart for cleanup even if it has user errors.
				ownedCart = payload?.cart ?? ownedCart;
				if (result.errors || !payload?.cart || payload.userErrors.length)
					throw new Error('The browser cart creation was not confirmed. No retry was made.');
				signal.throwIfAborted();
				options.onCart(payload.cart);
				return true;
			} finally {
				running = false;
			}
		},
		canRemove() {
			return (
				!running &&
				!removeAttempted &&
				!signal.aborted &&
				Boolean(ownedCart?.id && ownedCart.lines.nodes.length)
			);
		},
		async remove() {
			const target = ownedCart;
			if (running || removeAttempted || signal.aborted || !target?.id) return false;
			removeAttempted = true;
			running = true;
			try {
				const result = await client.graphql(cartQueries.cartLinesRemove, {
					variables: {
						cartId: target.id,
						lineIds: target.lines.nodes.map((line) => line.id),
					},
				});
				const payload = result.data?.cartLinesRemove;
				if (
					result.errors ||
					!payload?.cart ||
					payload.userErrors.length ||
					payload.cart.id !== target.id ||
					payload.cart.totalQuantity !== 0 ||
					payload.cart.lines.nodes.length !== 0
				)
					throw new Error('Cleanup was not confirmed. No retry was made.');
				signal.throwIfAborted();
				ownedCart = payload.cart;
				return true;
			} finally {
				running = false;
			}
		},
	};
}
